import React, { useCallback, useEffect, useMemo } from 'react';
import { ApolloProvider } from 'react-apollo';
import { ExperienceTrackerContext } from '@atlassian/experience-tracker';
import { AnalyticsProvider, DialogsProvider, I18nProvider, statusCodeHandlersProvider, useOrg, useSentryClient, useSite, useTeamCentralStoreApi } from '@atlassian/ptc-common';
import { useJiraCheckService } from '@atlassian/team-work';
import { TeamsWrapper } from '@atlassian/teams';
import { addBreadcrumb } from '@atlassian/teams-client/sentry';
import { getConfig, getEnv } from '@atlassian/teams-common/context-config';
import messagesConfig from '../../i18n/messagesConfig';
import AppRoutes from '../../routes';
import AppConfigProvider from '../AppConfigContext/AppConfigProvider';
import { ErrorBoundaryApp } from '../ErrorBoundary';
import * as Styled from './App.styled';
import AppInitialDataRequests from './AppInitialDataRequests';
export default function App(props) {
  const {
    appName,
    analytics,
    apolloClient,
    contextSpaPaths,
    contextEntryPoint,
    replace,
    push,
    locale,
    skipI18nProvider,
    addFlag,
    docTitle,
    env,
    history,
    statusCodeHandlers,
    cloudId,
    orgId,
    teamCentralHandlers,
    productFeatureFlags,
    invitePeopleHandlers,
    isSentryEnabled
  } = props;
  const sentryClient = useSentryClient(isSentryEnabled, {
    appName,
    env: getEnv()
  });
  const [, {
    setCloudId
  }] = useSite();
  const [, {
    setOrgId
  }] = useOrg();
  const [, {
    initTeamCentral
  }] = useTeamCentralStoreApi();
  const {
    isJiraAvailable
  } = useJiraCheckService(cloudId);
  const expTracker = analytics.expTracker();
  const analyticsClient = analytics.getAnalyticsWebClient();
  useEffect(() => {
    if (orgId) {
      analyticsClient.setOrgInfo(orgId);
    }
  }, [analyticsClient, orgId]);
  const appConfig = useMemo(() => ({
    env,
    appName,
    contextSpaPaths,
    contextEntryPoint,
    push,
    replace,
    addFlag,
    locale,
    docTitle,
    cloudId,
    orgId,
    productFeatureFlags,
    stargateRoot: getConfig().stargateRoot,
    isJiraAvailable
  }), [env, appName, contextSpaPaths, contextEntryPoint, isJiraAvailable, push, replace, addFlag, locale, docTitle, cloudId, orgId, productFeatureFlags]);

  // init statusCodeHandlersProvider
  useEffect(() => {
    if (statusCodeHandlers) {
      statusCodeHandlersProvider.setHandlers(statusCodeHandlers);
    }
  }, [statusCodeHandlers]);

  // save cloud id passed from consumers to a remote state management.
  useEffect(() => {
    setCloudId(cloudId);
  }, [cloudId, setCloudId]);

  // save org id passed from consumers to a remote state management.
  useEffect(() => {
    setOrgId(orgId);
  }, [orgId, setOrgId]);

  // Init and save various team central related data and preconditions
  useEffect(() => {
    initTeamCentral(appName, cloudId, teamCentralHandlers);
  }, [appName, cloudId, teamCentralHandlers, initTeamCentral]);
  const handleTransition = useCallback(pathname => {
    if (expTracker) {
      expTracker.abort({
        reason: 'Route changes.'
      });
    }
    addBreadcrumb(pathname);
  }, [expTracker]);
  return /*#__PURE__*/React.createElement(I18nProvider, {
    messagesConfig: messagesConfig,
    skip: typeof skipI18nProvider !== 'undefined' ? skipI18nProvider : !locale,
    locale: locale
  }, /*#__PURE__*/React.createElement(ErrorBoundaryApp, {
    analytics: analytics,
    appName: appName
  }, /*#__PURE__*/React.createElement(TeamsWrapper, {
    sentryClient: sentryClient,
    appConfig: appConfig
  }, /*#__PURE__*/React.createElement(AppConfigProvider, {
    env: env,
    contextSpaPaths: contextSpaPaths,
    contextEntryPoint: contextEntryPoint,
    docTitle: docTitle,
    appName: appName,
    addFlag: addFlag,
    push: push,
    locale: locale,
    replace: replace,
    history: history,
    cloudId: cloudId,
    orgId: orgId,
    isJiraAvailable: isJiraAvailable,
    teamCentralHandlers: teamCentralHandlers,
    productFeatureFlags: productFeatureFlags,
    invitePeopleHandlers: invitePeopleHandlers
  }, /*#__PURE__*/React.createElement(AnalyticsProvider, {
    analytics: analytics
  }, /*#__PURE__*/React.createElement(ExperienceTrackerContext.Provider, {
    value: analytics.expTracker()
  }, /*#__PURE__*/React.createElement(Styled.RelativeWrapperOfHeaderImage, null, /*#__PURE__*/React.createElement(ApolloProvider, {
    client: apolloClient
  }, /*#__PURE__*/React.createElement(DialogsProvider, null, /*#__PURE__*/React.createElement(AppInitialDataRequests, {
    appName: appName
  }, /*#__PURE__*/React.createElement(AppRoutes, {
    contextEntryPoint: contextEntryPoint,
    history: history,
    onTransition: handleTransition
  })))))))))));
}